import type { ComponentProps, ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { LayoutPickerSection, type SelectPostLayoutFn } from '@/components/layouts/LayoutPickerSection';
import { LayoutSettingsPanel } from '@/components/layouts/LayoutSettingsPanel';
import SubtitleEditor, { type SubtitleEditorProps } from '@/components/SubtitleEditor';
import { SubtitleLogoEditor } from '@/components/subtitles/SubtitleLogoEditor';
import { PaletteEditor, type PaletteEditorProps } from '@/components/palette/PaletteEditor';

type LayoutSettingsProps = ComponentProps<typeof LayoutSettingsPanel>;

export type StyleSidePanelProps = SubtitleEditorProps & {
  uniqueSpeakerCount: number;
  selectedSpeakerLayout: number;
  onSelectSpeakerLayout: (n: number) => void;
  postLayout: PostLayoutConfig | null;
  onSelectPostLayout: SelectPostLayoutFn;
  /** Speaker-count options offered by the picker. Editor: [1]. */
  speakerOptions?: number[];
  layoutFields: LayoutSettingsProps['fields'];
  layoutConfig: PostLayoutConfig | null;
  onLayoutFieldChange: LayoutSettingsProps['onChange'];
  videoFrameSource?: LayoutSettingsProps['videoFrameSource'];
  disabledKeys?: LayoutSettingsProps['disabledKeys'];
  fieldBinds?: LayoutSettingsProps['fieldBinds'];
  onFieldBindChange?: LayoutSettingsProps['onFieldBindChange'];
  bindContext?: LayoutSettingsProps['bindContext'];
  videoTargetDurationSec?: LayoutSettingsProps['videoTargetDurationSec'];
  /** Templates store a randomize flag instead of baking one shuffle. */
  deferRandomFillers?: boolean;
  /** Campaign lock card. When set, replaces the layout picker. */
  enforcedNotice?: ReactNode;
  palette?: Pick<PaletteEditorProps, 'value' | 'onChange'> | null;
  /** Portrait gate for picker + settings. Defaults to true (template page). */
  showLayoutControls?: boolean;
  /** Whether to render SubtitleEditor. Defaults to true. */
  showSubtitleEditor?: boolean;
  /**
   * Optional grouping wrapper after layout controls.
   * ProjectEditor uses this to put SubtitleEditor back inside the subtitles card.
   * When omitted, the subtitle node renders in place (template create/edit).
   */
  afterLayout?: (subtitle: ReactNode) => ReactNode;
  /** Optional slot before layout controls (project editor: Templates). */
  beforeLayout?: ReactNode;
};

export const StyleSidePanel = ({
  uniqueSpeakerCount,
  selectedSpeakerLayout,
  onSelectSpeakerLayout,
  postLayout,
  onSelectPostLayout,
  speakerOptions = [1],
  layoutFields,
  layoutConfig,
  onLayoutFieldChange,
  videoFrameSource,
  disabledKeys,
  fieldBinds,
  onFieldBindChange,
  bindContext,
  videoTargetDurationSec,
  deferRandomFillers,
  enforcedNotice,
  palette = null,
  showLayoutControls = true,
  showSubtitleEditor = true,
  afterLayout,
  beforeLayout,
  ...subtitleProps
}: StyleSidePanelProps) => {
  const subtitleNode = showSubtitleEditor ? (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-border/50" data-testid="subtitle-captions-card">
        <SubtitleEditor {...subtitleProps} includeLogo={false} />
      </div>
      <div className="overflow-hidden rounded-lg border border-border/50" data-testid="subtitle-logo-card">
        <SubtitleLogoEditor
          logoData={subtitleProps.logoData}
          onLogoChange={subtitleProps.onLogoChange}
          onLogoUpload={subtitleProps.onLogoUpload}
          isUploadingLogo={subtitleProps.isUploadingLogo}
        />
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-4 lg:space-y-6">
      {palette && (
        <PaletteEditor value={palette.value} onChange={palette.onChange} />
      )}

      {beforeLayout}

      {showLayoutControls && (
        enforcedNotice ?? (
          <Card className="p-3 sm:p-4" data-testid="layout-card">
            <LayoutPickerSection
              uniqueSpeakerCount={uniqueSpeakerCount}
              speakerOptions={speakerOptions}
              selectedSpeakerLayout={selectedSpeakerLayout}
              onSelectSpeakerLayout={onSelectSpeakerLayout}
              postLayout={postLayout}
              onSelectPostLayout={onSelectPostLayout}
              embedded
            />
            {layoutConfig && (
              <div className="mt-3 border-t border-border/60 pt-3">
                <LayoutSettingsPanel
                  fields={layoutFields}
                  config={layoutConfig}
                  onChange={onLayoutFieldChange}
                  videoFrameSource={videoFrameSource}
                  disabledKeys={disabledKeys}
                  fieldBinds={fieldBinds}
                  onFieldBindChange={onFieldBindChange}
                  bindContext={bindContext}
                  videoTargetDurationSec={videoTargetDurationSec}
                  deferRandomFillers={deferRandomFillers}
                />
              </div>
            )}
          </Card>
        )
      )}

      {afterLayout ? afterLayout(subtitleNode) : subtitleNode}
    </div>
  );
};
